import { ArrowBack } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaHookFormConfirmCloseDialog from "~/modules/app/components/GaiaHookFormConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import { MapPanelHeader, MapPanelScrollableArea } from "~/modules/map/components/MapPanel";
import MapEditorFeatureDetailTitle from "~/modules/mapEditor/components/MapEditorFeatureDetailTitle";
import { ObservationCardFragment } from "~/modules/mapEditor/components/ObservationCard/fragments.generated";

import EcologicalFunctionActionsBox from "./components/EcologicalFunctionActionsBox";
import { LinkableObservationsList } from "./components/LinkableObservationsList";
import { LinkedObservationsList } from "./components/LinkedObservationsList";
import { MapEditorEcologicalFunctionForm } from "./components/MapEditorEcologicalFunctionForm";
import { MapEditorEcologicalFunctionPanelEcologicalFunctionFragment } from "./fragments.generated";
import { useDeleteEcologicalFunctionMutation } from "./useDeleteEcologicalFunctionMutation";
import { useLinkObservationToEcologicalFunctionMutation } from "./useLinkObservationToEcologicalFunctionMutation";
import { useSuspenseEcologicalFunctionEditableQuery } from "./useSuspenseEcologicalFunctionEditableQuery";
import { useUnlinkObservationFromEcologicalFunctionMutation } from "./useUnlinkObservationFromEcologicalFunctionMutation";
import { EcologicalFunctionUpdateFormValues, useUpdateEcologicalFunctionMutation } from "./useUpdateEcologicalFunctionMutation";

export interface MapEditorEcologicalFunctionPanelEditableProps {
    backHref: string;
    createHrefToObservation: (element: ObservationCardFragment) => string;
    mapEditorFeatureId: string;
    onEcologicalFunctionDeleted?: () => Promise<void> | void;
    title: string;
}

export function MapEditorEcologicalFunctionPanelEditable({
    backHref,
    createHrefToObservation,
    mapEditorFeatureId,
    onEcologicalFunctionDeleted,
    title,
}: MapEditorEcologicalFunctionPanelEditableProps) {
    const { t } = useTranslation("mapEditor");
    const navigate = useNavigate();

    const { data } = useSuspenseEcologicalFunctionEditableQuery(mapEditorFeatureId);
    const ecologicalFunction = data.mapEditorFeatureGetById.data as MapEditorEcologicalFunctionPanelEcologicalFunctionFragment;
    const linkableObservations = data.ecologicalFunctionObservationGetAllLinkable;

    const [updateAction, { error: updateError }] = useUpdateEcologicalFunctionMutation();
    const [deleteAction, { loading: isDeleting }] = useDeleteEcologicalFunctionMutation();
    const [linkAction, { loading: isLinking }] = useLinkObservationToEcologicalFunctionMutation();
    const [unlinkAction] = useUnlinkObservationFromEcologicalFunctionMutation();

    const { handleSubmit, control, watch, reset, setError } = useForm<EcologicalFunctionUpdateFormValues>({
        values: createInitialFormData(ecologicalFunction),
        disabled: isDeleting,
    });

    useApplyErrorToForm(updateError, setError);

    const handleSubmitHof = handleSubmit(async values => {
        await updateAction(ecologicalFunction.id, ecologicalFunction.createdAt, values);
    });

    const handleDeleteClick = async () => {
        await deleteAction(ecologicalFunction.featureId);
        await onEcologicalFunctionDeleted?.();
        navigate(backHref, { relative: "path" });
    };

    return (
        <>
            <MapPanelHeader>
                <IconButton component={Link} color="inherit" relative="path" to={backHref}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h6" flex={1}>
                    {title}
                </Typography>
            </MapPanelHeader>
            <Box component="form" flex={1} minHeight={0} display="flex" flexDirection="column" onSubmit={handleSubmitHof}>
                <GaiaHookFormConfirmCloseDialog control={control} />

                <MapPanelScrollableArea>
                    <Stack gap={2} padding={2}>
                        <MapEditorFeatureDetailTitle
                            title={ecologicalFunction.researchType.name}
                            user={ecologicalFunction.createdByUser}
                        />
                        <MapEditorEcologicalFunctionForm
                            control={control}
                            ecologicalFunction={ecologicalFunction}
                            locationFormTemplate={data.settingsFormTemplateGetMasterForm}
                            watch={watch}
                        />
                        <Stack direction="row" gap={1} alignItems="center">
                            <GaiaDeleteButton
                                label={t("MapEditorFeatureDetails.featureDelete.btnDelete")}
                                isLoading={isDeleting}
                                onDelete={handleDeleteClick}
                            />
                        </Stack>
                        {ecologicalFunction.observations.length > 0 && (
                            <LinkedObservationsList
                                createHrefToObservation={createHrefToObservation}
                                observations={ecologicalFunction.observations}
                                onUnlinkClick={async observationId => {
                                    await unlinkAction(ecologicalFunction.id, observationId);
                                }}
                            />
                        )}
                        <LinkableObservationsList
                            createTo={createHrefToObservation}
                            observations={linkableObservations}
                            onLinkEcologicalFunctionWithObservationClick={async observationId => {
                                await linkAction(ecologicalFunction.id, observationId);
                            }}
                            loading={isLinking}
                        />
                    </Stack>
                </MapPanelScrollableArea>

                <EcologicalFunctionActionsBox control={control} onCancel={() => reset()} />
            </Box>
        </>
    );
}

function createInitialFormData(
    ef: MapEditorEcologicalFunctionPanelEcologicalFunctionFragment,
): EcologicalFunctionUpdateFormValues {
    return {
        answers: ef.answers ?? {},
        ecologicalFunctionTypeId: ef.ecologicalFunctionTypeId,
        speciesIds: ef.speciesList.map(species => species.id),
        medias: ef.medias,
        preciseLocation: ef.preciseLocation ?? {},
    };
}
